import React, { useState, useContext, useEffect } from "react";
import { gql, useMutation, useQuery, useApolloClient } from "@apollo/client";
import {
  LoadingView,
  NotificationContext,
  InvalidReasons,
  ToggleSwitch,
} from "@solsys/solsys-reactjs";
import SaveButton from "../elements/SaveButton";
import {
  useCreateIdempotencyKeys,
  fingerprintProperties,
  mutationInput,
} from "../../utils/idempotency";
import { isAdministrator } from "../../utils/roles";
import { useAuth } from "../../contexts/AuthContext";
import RegionDropDown from "../../dropdowns/RegionDropDown";
import CustomerDropDown from "../../dropdowns/CustomerDropDown";

const API_URL = process.env.REACT_APP_API_URL || "RUNTIME_REPLACE_REACT_APP_API_URL";

const ROLES = ["administrator", "technician", "customer"];

const userQuery = gql`
  query userById($user_list_request: user_list_request) {
    user(user_list_request: $user_list_request) {
      edges {
        id
        username
        display_name
        email
        customer_id
        enabled
        app_role {
          name
        }
        user2region {
          edges {
            id
            region_id
            region {
              id
              name
            }
          }
        }
      }
    }
  }
`;

const user2RegionMutation = gql`
  mutation user2RegionMutate($user2region_mutation_request: MutationInput) {
    user2region(user2region_mutation_request: $user2region_mutation_request) {
      id
      isNew
    }
  }
`;

const userMutation = gql`
  mutation userMutate($user_mutation_request: MutationInput) {
    user(user_mutation_request: $user_mutation_request) {
      id
      isNew
    }
  }
`;

function graphqlErrorMessage(error) {
  return error?.graphQLErrors?.[0]?.message || error?.message || "Request failed";
}

async function syncUserRegions({
  client,
  userId,
  selectedIds,
  existingEdges,
  createKeyFor,
}) {
  const selected = new Set(
    (Array.isArray(selectedIds) ? selectedIds : [selectedIds])
      .map((id) => Number(id))
      .filter((id) => !Number.isNaN(id) && id > 0)
  );
  const existing = existingEdges ?? [];
  const existingByRegion = new Map(
    existing.map((edge) => [Number(edge.region_id), edge])
  );

  for (const edge of existing) {
    const regionId = Number(edge.region_id);
    if (!selected.has(regionId)) {
      await client.mutate({
        mutation: user2RegionMutation,
        variables: {
          user2region_mutation_request: {
            id: edge.id,
            shouldDelete: true,
          },
        },
      });
    }
  }

  for (const regionId of selected) {
    if (!existingByRegion.has(regionId)) {
      await client.mutate({
        mutation: user2RegionMutation,
        variables: {
          user2region_mutation_request: mutationInput({
            properties: [
              { key: "user_id", number: Number(userId) },
              { key: "region_id", number: regionId },
            ],
            idempotencyKey: createKeyFor ? createKeyFor(regionId) : undefined,
          }),
        },
      });
    }
  }
}

export default function UserEdit({ id: entityId, action, navigate }) {
  const { addNotification } = useContext(NotificationContext);
  const { user: currentUser } = useAuth();
  const client = useApolloClient();
  const isAdd = action === "add";

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [display_name, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("technician");
  const [regionIds, setRegionIds] = useState([]);
  const [customerId, setCustomerId] = useState(null);
  const [regionLinks, setRegionLinks] = useState([]);
  const [invalidReasons, setInvalidReasons] = useState([]);
  const [saving, setSaving] = useState(false);
  const [isStatusUpdating, setIsStatusUpdating] = useState(false);
  const [isEnabled, setIsEnabled] = useState(true);
  const [resetPassword, setResetPassword] = useState("");
  const [resetPasswordConfirm, setResetPasswordConfirm] = useState("");
  const [resettingPassword, setResettingPassword] = useState(false);
  const [sendingResetEmail, setSendingResetEmail] = useState(false);
  const createKeys = useCreateIdempotencyKeys();
  const canSendResetEmail = isAdministrator(currentUser);

  const { loading, refetch } = useQuery(userQuery, {
    skip: isAdd || !entityId,
    fetchPolicy: "network-only",
    variables: {
      user_list_request: {
        filters: [{ field: "id", operator: "=", value: String(entityId) }],
        pagination: { limit: 1 },
      },
    },
    onCompleted: (data) => {
      const user = data?.user?.edges?.[0];
      if (!user) return;
      setUsername(user.username || "");
      setDisplayName(user.display_name || "");
      setEmail(user.email || "");
      setRole(user.app_role?.name || "technician");
      const regionEdges = user.user2region?.edges ?? [];
      setRegionLinks(regionEdges);
      setRegionIds(regionEdges.map((edge) => edge.region_id));
      setCustomerId(user.customer_id ?? null);
      const enabled =
        user.enabled === undefined || user.enabled === null
          ? true
          : Boolean(user.enabled);
      setIsEnabled(enabled);
    },
    onError: (error) => {
      console.error(error);
      addNotification("bad", graphqlErrorMessage(error) || "Failed to load user");
    },
  });

  const [mutateUser] = useMutation(userMutation);

  useEffect(() => {
    if (isAdd || !entityId) return;
    refetch();
  }, [entityId, isAdd, refetch]);

  const validate = () => {
    const reasons = [];
    if (isAdd) {
      if (!username.trim()) reasons.push("Username is required.");
      if (!password) reasons.push("Password is required.");
      if (!role) reasons.push("Role is required.");
    }
    setInvalidReasons(reasons);
    return reasons.length === 0;
  };

  const runMutation = async (request) => {
    try {
      await mutateUser({ variables: { user_mutation_request: request } });
      return true;
    } catch (error) {
      addNotification("bad", graphqlErrorMessage(error));
      return false;
    }
  };

  const handleCreate = async () => {
    if (!validate()) return;
    const properties = [
      { key: "username", string: username.trim() },
      { key: "password", string: password },
      { key: "role", string: role },
    ];
    if (display_name.trim()) {
      properties.push({ key: "display_name", string: display_name.trim() });
    }
    if (email.trim()) {
      properties.push({ key: "email", string: email.trim() });
    }
    if (role === "customer" && customerId) {
      properties.push({ key: "customer_id", number: Number(customerId) });
    }
    await createKeys.trackSave(async () => {
      try {
        const result = await mutateUser({
          variables: {
            user_mutation_request: mutationInput({
              properties,
              idempotencyKey: createKeys.keyFor("user", fingerprintProperties(properties)),
            }),
          },
        });
        const newUserId = result?.data?.user?.id;
        if (role === "technician" && newUserId && regionIds.length > 0) {
          try {
            await syncUserRegions({
              client,
              userId: newUserId,
              selectedIds: regionIds,
              existingEdges: [],
              createKeyFor: (regionId) =>
                createKeys.keyFor(`region:${regionId}`, `${newUserId}:${regionId}`),
            });
          } catch (error) {
            console.error(error);
            addNotification("bad", "User created but region assignment failed");
            navigate({ action: "list", id: null, search: {} });
            return;
          }
        }
        addNotification("good", "User created");
        createKeys.resetAfterSuccess();
        navigate({ action: "list", id: null, search: {} });
      } catch (error) {
        addNotification("bad", graphqlErrorMessage(error));
      }
    });
  };

  const handleUpdateUserInfo = async () => {
    if (!entityId) return;
    setSaving(true);
    try {
      const ok = await runMutation({
        id: String(entityId),
        properties: [
          { key: "display_name", string: display_name?.trim() || "" },
          { key: "email", string: email?.trim() || "" },
          { key: "role", string: role },
          { key: "enabled", number: isEnabled ? 1 : 0 },
          { key: "customer_id", number: role === "customer" && customerId ? Number(customerId) : null },
        ],
      });
      if (!ok) return;

      if (role === "technician") {
        await syncUserRegions({
          client,
          userId: entityId,
          selectedIds: regionIds,
          existingEdges: regionLinks,
          createKeyFor: (regionId) =>
            createKeys.keyFor(`region:${regionId}`, `${entityId}:${regionId}`),
        });
      } else if (regionLinks.length > 0) {
        await syncUserRegions({
          client,
          userId: entityId,
          selectedIds: [],
          existingEdges: regionLinks,
        });
        setRegionIds([]);
        setRegionLinks([]);
      }

      addNotification("good", "User info updated");
      refetch();
    } finally {
      setSaving(false);
    }
  };

  const handleResetPassword = async () => {
    if (!resetPassword.trim()) {
      addNotification("bad", "Enter a new password.");
      return;
    }
    if (resetPassword !== resetPasswordConfirm) {
      addNotification("bad", "Passwords do not match.");
      return;
    }
    setResettingPassword(true);
    try {
      const ok = await runMutation({
        id: String(entityId),
        properties: [{ key: "password", string: resetPassword }],
      });
      if (!ok) return;
      addNotification("good", "Password reset.");
      setResetPassword("");
      setResetPasswordConfirm("");
    } finally {
      setResettingPassword(false);
    }
  };

  const handleSendResetEmail = async () => {
    const targetEmail = (email || "").trim();
    if (!targetEmail) {
      addNotification("bad", "User email is required to send reset password email.");
      return;
    }
    setSendingResetEmail(true);
    try {
      const res = await fetch(`${API_URL}/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email: targetEmail }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 429) {
        const retry = Number(data.retryAfterSeconds || 0);
        const suffix = retry > 0 ? ` Try again in ${retry}s.` : "";
        throw new Error((data.error || "Too many reset requests.") + suffix);
      }
      if (!res.ok) throw new Error(data.error || "Failed to send reset password email");
      addNotification(
        "good",
        data.message || "If an account with that email exists, a password reset link has been sent."
      );
    } catch (e) {
      addNotification("bad", e.message || "Failed to send reset password email");
    } finally {
      setSendingResetEmail(false);
    }
  };

  const updateEnabledState = async (nextEnabled) => {
    if (!entityId) return;
    setIsStatusUpdating(true);
    try {
      const ok = await runMutation({
        id: String(entityId),
        properties: [{ key: "enabled", number: nextEnabled ? 1 : 0 }],
      });
      if (!ok) return;
      setIsEnabled(nextEnabled);
      addNotification("good", nextEnabled ? "User enabled" : "User disabled");
    } finally {
      setIsStatusUpdating(false);
    }
  };

  if (loading && !isAdd) return <LoadingView />;

  const sectionStyle = {
    padding: "16px",
    border: "1px solid var(--card-border)",
    borderRadius: 8,
    background: "var(--card-bg)",
    marginBottom: 16,
  };

  const gridStyle = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
    gap: 14,
    alignItems: "start",
  };

  const userInfoGridStyle = {
    ...gridStyle,
    gridTemplateColumns: "1fr 1fr 1fr",
  };

  return (
    <div className="add-edit">
      <h2>{ isAdd ? "Create user" : "Edit user" }</h2>

      { invalidReasons.length > 0 && <InvalidReasons reasons={ invalidReasons } /> }

      <div className="edit-section" style={ sectionStyle }>
        <h3 style={ { marginTop: 0, marginBottom: 12 } }>User Info</h3>
        <div style={ userInfoGridStyle }>
          <div className="edit-property">
            <label>Username *</label>
            <input
              value={ username }
              onChange={ (e) => setUsername(e.target.value) }
              disabled={ !isAdd }
              placeholder={ isAdd ? "Required" : "" }
            />
          </div>
          { isAdd && (
            <div className="edit-property">
              <label>Password *</label>
              <input
                type="password"
                value={ password }
                onChange={ (e) => setPassword(e.target.value) }
                placeholder="Required"
              />
            </div>
          ) }
          <div className="edit-property">
            <label>Role{ isAdd ? " *" : "" }</label>
            <select value={ role } onChange={ (e) => setRole(e.target.value) } disabled={ saving }>
              { ROLES.map((r) => (
                <option key={ r } value={ r }>{ r }</option>
              )) }
            </select>
          </div>
          { role === "technician" && (
            <div className="edit-property" style={ { gridColumn: "1 / -1" } }>
              <label>Regions</label>
              <RegionDropDown
                selected={ regionIds }
                onSelect={ setRegionIds }
                searchEnabled
                multiple
              />
            </div>
          ) }
          { role === "customer" && (
            <div className="edit-property" style={ { gridColumn: "1 / -1" } }>
              <label>Customer</label>
              <CustomerDropDown
                selected={ customerId }
                onSelect={ setCustomerId }
                searchEnabled
              />
            </div>
          ) }
          <div className="edit-property">
            <label>Display name</label>
            <input
              value={ display_name }
              onChange={ (e) => setDisplayName(e.target.value) }
            />
          </div>
          <div className="edit-property">
            <label>Email</label>
            <input
              type="email"
              value={ email }
              onChange={ (e) => setEmail(e.target.value) }
            />
          </div>
          { !isAdd && (
            <div className="edit-property">
              <label>Active</label>
              <ToggleSwitch
                checked={ isEnabled }
                onChange={ () => updateEnabledState(!isEnabled) }
                disabled={ isStatusUpdating }
                size={ {
                  width: 60,
                  height: 32,
                  knob: 24,
                  padding: 4,
                } }
              />
            </div>
          ) }
          { !isAdd && (
            <div className="edit-property" style={ { gridColumn: "1 / -1" } }>
              <button
                type="button"
                onClick={ handleUpdateUserInfo }
                disabled={ saving || isStatusUpdating }
              >
                { saving ? "Saving…" : "Update user info" }
              </button>
            </div>
          ) }
        </div>
      </div>

      { !isAdd && (
        <>
          <div className="edit-section" style={ sectionStyle }>
            <h3 style={ { marginTop: 0, marginBottom: 12 } }>Security</h3>
            <div style={ gridStyle }>
              <div className="edit-property">
                <label>New password</label>
                <input
                  type="password"
                  value={ resetPassword }
                  onChange={ (e) => setResetPassword(e.target.value) }
                  placeholder="New password"
                  autoComplete="new-password"
                />
              </div>
              <div className="edit-property">
                <label>Confirm password</label>
                <input
                  type="password"
                  value={ resetPasswordConfirm }
                  onChange={ (e) => setResetPasswordConfirm(e.target.value) }
                  placeholder="Confirm new password"
                  autoComplete="new-password"
                />
              </div>
              <div className="edit-property" style={ { gridColumn: "1 / -1" } }>
                <button
                  type="button"
                  onClick={ handleResetPassword }
                  disabled={ resettingPassword || !resetPassword.trim() }
                >
                  { resettingPassword ? "Resetting…" : "Reset password" }
                </button>
              </div>
              { canSendResetEmail && (
                <div className="edit-property" style={ { gridColumn: "1 / -1" } }>
                  <button
                    type="button"
                    onClick={ handleSendResetEmail }
                    disabled={ sendingResetEmail || !(email || "").trim() }
                  >
                    { sendingResetEmail ? "Sending…" : "Send reset password email"}
                  </button>
                </div>
              ) }
            </div>
          </div>

        </>
      ) }

      { isAdd && (
        <div className="edit-section" style={ sectionStyle }>
          <SaveButton onClick={ handleCreate } saving={ createKeys.isSaving }>
            Create user
          </SaveButton>
        </div>
      ) }
    </div>
  );
}
